import { isBefore, parse } from 'date-fns';

import type { DateGranularity } from '../contexts/FilterProvider';
import { type Filter, FilterId, FilterType } from '../types/filters';
import { getFormattedDateString, localToUTC } from './dateUtils';

export const getDateFromFilters = ({
  filters,
  granularity,
  isSnapshot = false,
}: {
  filters: Filter[];
  granularity: DateGranularity;
  isSnapshot?: boolean;
}) => {
  const snapshotDateFilter = filters.find(
    (filter) =>
      filter.id === FilterId.SNAPSHOT_DATE && filter.type === FilterType.DATE
  );
  const dateRangeFilter = filters.find(
    (filter) =>
      filter.id === FilterId.DATE_RANGE && filter.type === FilterType.DATE_RANGE
  );

  if (isSnapshot) {
    if (
      snapshotDateFilter &&
      snapshotDateFilter.type === FilterType.DATE &&
      snapshotDateFilter.value
    ) {
      return {
        start_date: getFormattedDateString(
          snapshotDateFilter.value,
          granularity
        ),
        end_date: getFormattedDateString(snapshotDateFilter.value, granularity),
      };
    }

    if (
      dateRangeFilter &&
      dateRangeFilter.type === FilterType.DATE_RANGE &&
      dateRangeFilter.max
    ) {
      return {
        start_date: getFormattedDateString(dateRangeFilter.max, granularity),
        end_date: getFormattedDateString(dateRangeFilter.max, granularity),
      };
    }

    return null;
  }

  if (
    dateRangeFilter &&
    dateRangeFilter.type === FilterType.DATE_RANGE &&
    dateRangeFilter.min &&
    dateRangeFilter.max
  ) {
    return {
      start_date: getFormattedDateString(dateRangeFilter.min, granularity),
      end_date: getFormattedDateString(dateRangeFilter.max, granularity),
    };
  }

  if (
    snapshotDateFilter &&
    snapshotDateFilter.type === FilterType.DATE &&
    snapshotDateFilter.value
  ) {
    return {
      start_date: getFormattedDateString(snapshotDateFilter.value, granularity),
      end_date: getFormattedDateString(snapshotDateFilter.value, granularity),
    };
  }

  return null;
};

export const clampDates = ({
  start_date,
  end_date,
  minStartDate,
  dateGranularity = 'month',
}: {
  start_date?: string;
  end_date?: string;
  minStartDate: Date;
  dateGranularity: DateGranularity;
}) => {
  const parseFormat = dateGranularity === 'month' ? 'yyyy-MM' : 'yyyy-MM-dd';
  const startDate = start_date
    ? parse(start_date, parseFormat, new Date())
    : minStartDate;
  const limitedStartDate = isBefore(startDate, minStartDate)
    ? minStartDate
    : startDate;

  const endDate = end_date
    ? parse(end_date, parseFormat, new Date())
    : new Date();
  const limitedEndDate = isBefore(endDate, limitedStartDate)
    ? limitedStartDate
    : endDate;

  return {
    start_date: getFormattedDateString(
      localToUTC(limitedStartDate),
      dateGranularity
    ),
    end_date: getFormattedDateString(
      localToUTC(limitedEndDate),
      dateGranularity
    ),
  };
};
